import React, {
  useEffect,
  useState,
} from "react";

import {
  useNavigate,
  useParams,
} from "react-router-dom";

import {
  useDispatch,
  useSelector,
} from "react-redux";

import {
  doc,
  getDoc,
} from "firebase/firestore";

import {
  ref,
  getBlob,
} from "firebase/storage";

import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  Flex,
  Heading,
  HStack,
  Input,
  Select,
  SimpleGrid,
  Spinner,
  Stack,
  Text,
  useToast,
} from "@chakra-ui/react";

import {
  db,
  storage,
} from "../../firebase/config";

import {
  PROJECT_STATUS_CONFIG,
  DEFAULT_PROJECT_STATUS_CONFIG,
} from "./projectStatuses";

import {
  archiveProject,
  restoreProject,
  updateProjectStatus,
  updateProjectPriority,
  updateProject,
  uploadProjectAttachment,
} from "./projectActions";

import ProjectFormModal from "./ProjectFormModal";

import InternalNotesDashboard from "./internalNotes/InternalNotesDashboard";
import RepliesDashboard from "./replies/RepliesDashboard";
import AddInternalNoteForm from "./internalNotes/AdddInternalNote";
import AddReplyForm from "./replies/AddReplyForm";
const PROJECT_PRIORITIES = [
  "Unassigned",
  "Low",
  "Normal",
  "High",
  "Urgent",
];

const formatDate = (value) => {
  if (!value) {
    return "N/A";
  }

  try {
    const date =
      typeof value.toDate === "function"
        ? value.toDate()
        : value.seconds
        ? new Date(value.seconds * 1000)
        : new Date(value);

    if (Number.isNaN(date.getTime())) {
      return "N/A";
    }

    return date.toLocaleString();
  } catch (error) {
    return "N/A";
  }
};

const Project = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const toast = useToast();

  const [project, setProject] =
    useState(null);

  const [isLoading, setIsLoading] =
    useState(true);

  const [loadError, setLoadError] =
    useState(null);

  const [isEditOpen, setIsEditOpen] =
    useState(false);

  const [uploading, setUploading] =
    useState(false);

  const [file, setFile] =
    useState(null);

  const [archiveActionLoading, setArchiveActionLoading] =
    useState(false);
    const [
  priorityActionLoading,
  setPriorityActionLoading,
] = useState(false);

  const { user } = useSelector(
    (state) => state.auth
  );

  const profile = useSelector(
    (state) => state.profile.profile
  );

  const isAdmin =
    profile?.isAdmin === true;

  useEffect(() => {
    let isActive = true;

    const fetchProject = async () => {
      setIsLoading(true);
      setLoadError(null);

      try {
        const projectRef = doc(
          db,
          "projects",
          id
        );

        const snapshot =
          await getDoc(projectRef);

        if (!isActive) {
          return;
        }

        if (!snapshot.exists()) {
          setProject(null);
          setLoadError("not-found");
          return;
        }

        setProject({
          id: snapshot.id,
          ...snapshot.data(),
        });
      } catch (error) {
        if (!isActive) {
          return;
        }

        console.error(
          "Project could not be loaded:",
          error
        );

        setProject(null);

        if (
          error.code ===
          "permission-denied"
        ) {
          setLoadError(
            "permission-denied"
          );
        } else {
          setLoadError("load-failed");
        }
      } finally {
        if (isActive) {
          setIsLoading(false);
        }
      }
    };

    if (!id) {
      setLoadError("not-found");
      setIsLoading(false);

      return () => {
        isActive = false;
      };
    }

    fetchProject();

    return () => {
      isActive = false;
    };
  }, [id]);

  const isArchived =
    project?.archived === true;

  const handleStatusChange = async (
    event
  ) => {
    if (isArchived) {
      return;
    }

    const newStatus =
      event.target.value;

    try {
      await dispatch(
        updateProjectStatus(
          id,
          newStatus
        )
      );

      setProject(
        (previousProject) => ({
          ...previousProject,
          status: newStatus,
        })
      );

      toast({
        title: "Status updated",
        description: `Project marked as "${newStatus}".`,
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error(
        "Project status update failed:",
        error
      );

      toast({
        title:
          "Status could not be updated",
        description:
          "Please try again. If the problem continues, contact an administrator.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };
const handlePriorityChange = async (
  event
) => {
  if (!isAdmin || isArchived) {
    return;
  }

  const newPriority = event.target.value;
  const currentPriority =
    project.priority || "Unassigned";

  if (newPriority === currentPriority) {
    return;
  }

  setPriorityActionLoading(true);

  try {
    await dispatch(
      updateProjectPriority(
        id,
        newPriority
      )
    );

    setProject(
      (previousProject) => ({
        ...previousProject,
        priority: newPriority,
      })
    );

    toast({
      title: "Priority updated",
      description:
        `Project priority changed to "${newPriority}".`,
      status: "success",
      duration: 3000,
      isClosable: true,
    });
  } catch (error) {
    console.error(
      "Project priority update failed:",
      error
    );

    toast({
      title:
        "Priority could not be updated",
      description:
        "Please try again. If the problem continues, contact an administrator.",
      status: "error",
      duration: 5000,
      isClosable: true,
    });
  } finally {
    setPriorityActionLoading(false);
  }
};
  const handleSave = async (
    updatedData
  ) => {
    if (isArchived) {
      toast({
        title: "Project is archived",
        description:
          "Restore this project before making changes.",
        status: "warning",
        duration: 4000,
        isClosable: true,
      });

      return;
    }

    try {
      await dispatch(
        updateProject(id, updatedData)
      );

      setProject(
        (previousProject) => ({
          ...previousProject,
          ...updatedData,
        })
      );

      toast({
        title: "Project updated",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error(
        "Project update failed:",
        error
      );

      toast({
        title:
          "Project could not be updated",
        description:
          "Please try again. If the problem continues, contact an administrator.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });

      throw error;
    }
  };

  const handleArchive = async () => {
    if (
      !isAdmin ||
      !project ||
      isArchived
    ) {
      return;
    }

    const confirmed = window.confirm(
      `Archive "${project.projectTitle || "this project"}"?\n\n` +
        "The project, replies, internal notes, and files will be preserved. " +
        "The project will become read-only until an administrator restores it."
    );

    if (!confirmed) {
      return;
    }

    setArchiveActionLoading(true);

    try {
      await dispatch(
        archiveProject(
          project.id,
          profile
        )
      );

      setProject(
        (previousProject) => ({
          ...previousProject,
          archived: true,
          archivedAt: new Date(),
          archivedBy: {
            uid: user?.uid || "",
            email:
              user?.email
                ?.trim()
                .toLowerCase() || "",
            name:
              profile?.displayName ||
              [
                profile?.firstName,
                profile?.lastName,
              ]
                .filter(Boolean)
                .join(" ")
                .trim() ||
              user?.displayName ||
              "",
          },
        })
      );

      setIsEditOpen(false);
      setFile(null);

      toast({
        title: "Project archived",
        description:
          "The project and all related records have been preserved.",
        status: "success",
        duration: 4000,
        isClosable: true,
      });
    } catch (error) {
      console.error(
        "Project archive failed:",
        error
      );

      toast({
        title:
          "Project could not be archived",
        description:
          "Please try again. If the problem continues, contact an administrator.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setArchiveActionLoading(false);
    }
  };

  const handleRestore = async () => {
    if (
      !isAdmin ||
      !project ||
      !isArchived
    ) {
      return;
    }

    const confirmed = window.confirm(
      `Restore "${project.projectTitle || "this project"}"?\n\n` +
        "The project will become active and editable again."
    );

    if (!confirmed) {
      return;
    }

    setArchiveActionLoading(true);

    try {
      await dispatch(
        restoreProject(
          project.id,
          profile
        )
      );

      setProject(
        (previousProject) => ({
          ...previousProject,
          archived: false,
          restoredAt: new Date(),
          restoredBy: {
            uid: user?.uid || "",
            email:
              user?.email
                ?.trim()
                .toLowerCase() || "",
            name:
              profile?.displayName ||
              [
                profile?.firstName,
                profile?.lastName,
              ]
                .filter(Boolean)
                .join(" ")
                .trim() ||
              user?.displayName ||
              "",
          },
        })
      );

      toast({
        title: "Project restored",
        description:
          "The project is active and can be updated again.",
        status: "success",
        duration: 4000,
        isClosable: true,
      });
    } catch (error) {
      console.error(
        "Project restore failed:",
        error
      );

      toast({
        title:
          "Project could not be restored",
        description:
          "Please try again. If the problem continues, contact an administrator.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setArchiveActionLoading(false);
    }
  };

  const handleAttachmentDownload =
    async (attachment) => {
      if (!attachment.path) {
        if (attachment.url) {
          window.open(
            attachment.url,
            "_blank",
            "noopener,noreferrer"
          );

          return;
        }

        toast({
          title: "File unavailable",
          description:
            "This older attachment does not have a valid file location.",
          status: "error",
          duration: 4000,
          isClosable: true,
        });

        return;
      }

      try {
        const attachmentRef = ref(
          storage,
          attachment.path
        );

        const fileBlob =
          await getBlob(attachmentRef);

        const temporaryUrl =
          URL.createObjectURL(fileBlob);

        const downloadLink =
          document.createElement("a");

        downloadLink.href =
          temporaryUrl;

        downloadLink.download =
          attachment.name ||
          attachment.originalFileName ||
          "project-attachment";

        document.body.appendChild(
          downloadLink
        );

        downloadLink.click();
        downloadLink.remove();

        window.setTimeout(() => {
          URL.revokeObjectURL(
            temporaryUrl
          );
        }, 1000);
      } catch (error) {
        console.error(
          "Attachment download failed:",
          error
        );

        toast({
          title:
            "File could not be downloaded",
          description:
            "You may not have permission to access this file, or the file may no longer exist.",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }
    };

  const handleAttachmentUpload =
    async () => {
      if (isArchived) {
        toast({
          title: "Project is archived",
          description:
            "Restore this project before uploading files.",
          status: "warning",
          duration: 4000,
          isClosable: true,
        });

        return;
      }

      if (!file || !project?.id) {
        toast({
          title: "Choose a file first",
          status: "warning",
          duration: 3000,
          isClosable: true,
        });

        return;
      }

      setUploading(true);

      try {
        const newAttachment =
          await uploadProjectAttachment(
            project.id,
            file
          );

        setProject(
          (previousProject) => ({
            ...previousProject,
            attachments: [
              ...(
                previousProject.attachments ||
                []
              ),
              newAttachment,
            ],
          })
        );

        setFile(null);

        toast({
          title: "File uploaded",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
      } catch (error) {
        console.error(
          "Project attachment upload failed:",
          error
        );

        toast({
          title:
            "File could not be uploaded",
          description:
            "Please check the file and try again.",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      } finally {
        setUploading(false);
      }
    };

  const renderMessage = ({
    heading,
    message,
    color = "gray.700",
  }) => (
    <Flex
      justify="center"
      align="center"
      minH="60vh"
      px={6}
    >
      <Box
        width="100%"
        maxW="600px"
        p={8}
        bg="white"
        borderWidth="1px"
        borderRadius="lg"
        shadow="md"
        textAlign="center"
      >
        <Heading
          size="lg"
          color={color}
        >
          {heading}
        </Heading>

        <Text
          mt={4}
          color="gray.600"
        >
          {message}
        </Text>

        <Button
          mt={6}
          colorScheme="teal"
          onClick={() =>
            navigate("/projects")
          }
        >
          Back to Projects
        </Button>
      </Box>
    </Flex>
  );

  if (isLoading) {
    return (
      <Flex
        justify="center"
        align="center"
        minH="60vh"
        direction="column"
        gap={4}
      >
        <Spinner
          size="xl"
          color="teal.500"
        />

        <Text color="gray.600">
          Loading project…
        </Text>
      </Flex>
    );
  }

  if (loadError === "not-found") {
    return renderMessage({
      heading: "Project Not Found",
      message:
        "This project does not exist, or the link may be incorrect.",
    });
  }

  if (
    loadError ===
    "permission-denied"
  ) {
    return renderMessage({
      heading: "Access Denied",
      message:
        "You do not have permission to view this project.",
      color: "red.500",
    });
  }

  if (loadError || !project) {
    return renderMessage({
      heading:
        "Project Could Not Be Loaded",
      message:
        "Please try again. If the problem continues, contact an administrator.",
      color: "red.500",
    });
  }

  const email =
    user?.email
      ?.trim()
      .toLowerCase();

  const canView =
    isAdmin ||
    project.createdBy?.email
      ?.trim()
      .toLowerCase() === email ||
    project.contactEmail
      ?.trim()
      .toLowerCase() === email ||
    project.additionalEmails?.some(
      (additionalEmail) =>
        additionalEmail
          ?.trim()
          .toLowerCase() === email
    );

  if (!canView) {
    return renderMessage({
      heading: "Access Denied",
      message:
        "You do not have permission to view this project.",
      color: "red.500",
    });
  }

  const statusKey =
    project.status || "Open";

  const statusConfig =
    PROJECT_STATUS_CONFIG[
      statusKey
    ] ||
    DEFAULT_PROJECT_STATUS_CONFIG;

  const StatusIcon =
    statusConfig.icon;

  const projectNumber =
    project.projectNumber ||
    project.id?.slice(0, 9) ||
    "Unavailable";

  return (
    <Box
      p={{ base: 4, md: 6 }}
      mb={2}
      maxW="1200px"
      mx="auto"
    >
      {isArchived && (
        <Alert
          status="warning"
          variant="left-accent"
          mb={6}
          borderRadius="md"
          alignItems="flex-start"
        >
          <AlertIcon mt={1} />

          <Box>
            <Text fontWeight="bold">
              Archived project
            </Text>

            <Text fontSize="sm">
              This project is preserved
              as a read-only record.
              {isAdmin
                ? " Restore it before making any changes."
                : ""}
            </Text>
          </Box>
        </Alert>
      )}

      <Stack
        spacing={2}
        mb={6}
      >
        <HStack
          spacing={3}
          flexWrap="wrap"
        >
          <Text
            fontSize="sm"
            fontWeight="bold"
            color="gray.600"
            letterSpacing="wide"
          >
            Project #{projectNumber}
          </Text>

          {isArchived && (
            <Badge
              colorScheme="orange"
              fontSize="sm"
            >
              Archived
            </Badge>
          )}
        </HStack>

        <Heading
          fontSize={{
            base: "2xl",
            md: "3xl",
          }}
          color="teal.600"
        >
          {project.projectTitle ||
            "Untitled Project"}
        </Heading>
      </Stack>

      <SimpleGrid
        columns={{
          base: 1,
          md: 2,
          xl: 3,
        }}
        spacing={6}
      >
        <Box
          p={5}
          bg="white"
          shadow="md"
          borderRadius="md"
        >
          <Text
            fontWeight="bold"
            mb={3}
          >
            Project Details
          </Text>

          <Stack spacing={2}>
            <Text>
              <b>Parish:</b>{" "}
              {project.parish || "N/A"}
            </Text>

            <Text>
              <b>Pastor:</b>{" "}
              {project.pastor || "N/A"}
            </Text>

            <Text>
              <b>Contact:</b>{" "}
              {project.contactName ||
                "N/A"}
            </Text>

            <Text>
              <b>Phone:</b>{" "}
              {project.contactPhone ||
                "N/A"}
            </Text>

            <Text>
              <b>Email:</b>{" "}
              {project.contactEmail ||
                "N/A"}
            </Text>
          </Stack>
        </Box>

        <Box
          p={5}
          bg="white"
          shadow="md"
          borderRadius="md"
          maxH="60vh"
          overflowY="auto"
        >
          <Text
            fontWeight="bold"
            mb={3}
          >
            Description
          </Text>

          <Text
            fontSize="sm"
            whiteSpace="pre-wrap"
          >
            {project.projectDescription ||
              "No description provided."}
          </Text>
        </Box>

        <Box
          p={5}
          bg="white"
          shadow="md"
          borderRadius="md"
        >
          <Text
            fontWeight="bold"
            mb={3}
          >
            Project Information
          </Text>

          <Stack spacing={2}>
            <Text>
              <b>Permission:</b>{" "}
              {project.seekingPermission ||
                "N/A"}
            </Text>

            <Text>
              <b>Funding:</b>{" "}
              {project.fundingSource ||
                "N/A"}
            </Text>

            <Text>
              <b>Finance Council:</b>{" "}
              {project.financeCouncil ||
                "N/A"}
            </Text>

            <Text>
              <b>Lay Trustees:</b>{" "}
              {project.layTrustees ||
                "N/A"}
            </Text>

            <Box>
  <Text fontWeight="bold" mb={1}>
    Priority:
  </Text>

  {isAdmin && !isArchived ? (
    <Select
      value={
        project.priority ||
        "Unassigned"
      }
      onChange={
        handlePriorityChange
      }
      isDisabled={
        priorityActionLoading
      }
      bg="white"
      maxW="240px"
      aria-label="Project priority"
    >
      {PROJECT_PRIORITIES.map(
        (priority) => (
          <option
            key={priority}
            value={priority}
          >
            {priority}
          </option>
        )
      )}
    </Select>
  ) : (
    <Text>
      {project.priority ||
        "Unassigned"}
    </Text>
  )}
</Box>
          </Stack>
        </Box>

        <Box
          p={5}
          bg="white"
          shadow="md"
          borderRadius="md"
        >
          <Text
            fontWeight="bold"
            mb={3}
          >
            Project Metadata
          </Text>

          <Stack spacing={2}>
            <Text>
              <b>Created by Email:</b>{" "}
              {project.createdBy?.email ||
                "N/A"}
            </Text>

            <Text>
              <b>Created At:</b>{" "}
              {formatDate(
                project.createdAt
              )}
            </Text>

            <Text>
              <b>Additional Emails:</b>{" "}
              {project.additionalEmails
                ?.join(", ") || "N/A"}
            </Text>

            {isArchived && (
              <>
                <Text>
                  <b>Archived At:</b>{" "}
                  {formatDate(
                    project.archivedAt
                  )}
                </Text>

                <Text>
                  <b>Archived By:</b>{" "}
                  {project.archivedBy
                    ?.name ||
                    project.archivedBy
                      ?.email ||
                    "Administrator"}
                </Text>
              </>
            )}
          </Stack>
        </Box>

        <Box
          p={5}
          bg="white"
          shadow="md"
          borderRadius="md"
        >
          <Text
            fontWeight="bold"
            mb={3}
          >
            Project Status
          </Text>

          <Box
            p={3}
            borderRadius="md"
            bg={`${statusConfig.color}.50`}
            border="1px solid"
            borderColor={`${statusConfig.color}.300`}
          >
            <HStack
              spacing={2}
              mb={
                isAdmin &&
                !isArchived
                  ? 3
                  : 0
              }
            >
              {StatusIcon && (
                <StatusIcon
                  color={`${statusConfig.color}.500`}
                />
              )}

              <Text
                fontWeight="semibold"
                color={`${statusConfig.color}.700`}
              >
                {project.status ||
                  "Open"}
              </Text>
            </HStack>

            {isAdmin &&
              !isArchived && (
                <>
                  <Text
                    fontWeight="semibold"
                    mb={1}
                  >
                    Change Status:
                  </Text>

                  <Select
                    value={
                      project.status ||
                      "Open"
                    }
                    onChange={
                      handleStatusChange
                    }
                    width="100%"
                    maxW="280px"
                    bg="white"
                  >
                    {Object.keys(
                      PROJECT_STATUS_CONFIG
                    ).map((status) => (
                      <option
                        key={status}
                        value={status}
                      >
                        {status}
                      </option>
                    ))}
                  </Select>
                </>
              )}
          </Box>
        </Box>

        <Box
          p={5}
          bg="white"
          shadow="md"
          borderRadius="md"
        >
          <Text
            fontWeight="bold"
            mb={3}
          >
            Attachments
          </Text>

          <Stack
            spacing={2}
            mb={
              isArchived ? 0 : 4
            }
          >
            {project.attachments
              ?.length > 0 ? (
              project.attachments.map(
                (
                  attachment,
                  index
                ) => (
                  <Button
                    key={
                      attachment.path ||
                      attachment.url ||
                      `${
                        attachment.name ||
                        "attachment"
                      }-${index}`
                    }
                    variant="link"
                    colorScheme="teal"
                    justifyContent="flex-start"
                    width="fit-content"
                    height="auto"
                    whiteSpace="normal"
                    textAlign="left"
                    onClick={() =>
                      handleAttachmentDownload(
                        attachment
                      )
                    }
                  >
                    {attachment.name ||
                      attachment.originalFileName ||
                      "Download attachment"}
                  </Button>
                )
              )
            ) : (
              <Text
                fontSize="sm"
                color="gray.500"
              >
                No attachments have
                been added.
              </Text>
            )}
          </Stack>

          {!isArchived && (
            <Stack
              direction={{
                base: "column",
                sm: "row",
              }}
              spacing={2}
            >
              <Input
                type="file"
                onChange={(event) =>
                  setFile(
                    event.target.files?.[0] ||
                      null
                  )
                }
              />

              <Button
                isLoading={uploading}
                loadingText="Uploading"
                colorScheme="teal"
                onClick={
                  handleAttachmentUpload
                }
                flexShrink={0}
              >
                Upload
              </Button>
            </Stack>
          )}
        </Box>
      </SimpleGrid>

      <HStack
        spacing={3}
        mt={6}
        mb={6}
        flexWrap="wrap"
      >
        {!isArchived && (
          <Button
            colorScheme="blue"
            onClick={() =>
              setIsEditOpen(true)
            }
          >
            Edit Project
          </Button>
        )}

        {isAdmin &&
          !isArchived && (
            <Button
              colorScheme="orange"
              variant="outline"
              isLoading={
                archiveActionLoading
              }
              loadingText="Archiving"
              onClick={handleArchive}
            >
              Archive Project
            </Button>
          )}

        {isAdmin &&
          isArchived && (
            <Button
              colorScheme="green"
              isLoading={
                archiveActionLoading
              }
              loadingText="Restoring"
              onClick={handleRestore}
            >
              Restore Project
            </Button>
          )}
      </HStack>

      <ProjectFormModal
        isOpen={
          isEditOpen &&
          !isArchived
        }
        onClose={() =>
          setIsEditOpen(false)
        }
        project={project}
        onSave={handleSave}
      />

      {project.id && (
        <SimpleGrid
          columns={{
            base: 1,
            md: isAdmin ? 2 : 1,
          }}
          spacing={6}
        >
          <Box
            p={5}
            bg="white"
            shadow="md"
            borderRadius="md"
          >
            <Heading
              size="md"
              mb={4}
            >
              Replies
            </Heading>

            {!isArchived && (
              <Box mb={5}>
                <AddReplyForm
                  projectId={
                    project.id
                  }
                  currentUser={
                    profile
                  }
                />
              </Box>
            )}

            <RepliesDashboard
              projectId={project.id}
              readOnly={isArchived}
            />
          </Box>

          {isAdmin && (
            <Box
              p={5}
              bg="white"
              shadow="md"
              borderRadius="md"
            >
              <Heading
                size="md"
                mb={4}
              >
                Internal Notes
              </Heading>

              {!isArchived && (
                <Box mb={5}>
                  <AddInternalNoteForm
                    projectId={
                      project.id
                    }
                    currentUser={
                      profile
                    }
                  />
                </Box>
              )}

              <InternalNotesDashboard
                projectId={
                  project.id
                }
                currentUser={
                  profile
                }
                readOnly={
                  isArchived
                }
              />
            </Box>
          )}
        </SimpleGrid>
      )}
    </Box>
  );
};

export default Project;